import { c as _c } from "react/compiler-runtime";
import { animated } from '@react-spring/web';
import { Set } from 'immutable';
import React, { FunctionComponent, PropsWithChildren, useMemo, useRef, useState } from 'react';
import { Fade } from 'record-it-components/animation/Fade/Fade';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Spinner } from 'record-it-components/misc/Spinner/Spinner';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { PageLoadingOverlayContext, PageLoadingOverlayContextValue } from '../context/PageLoadingOverlayContext';
import styles from './AppPageLoadingOverlayContextProvider.module.css';
const defaultLoadingContext = Symbol('defaultLoadingContext');
export const AppPageLoadingOverlayContextProvider: FunctionComponent<PropsWithChildren> = React.memo(props => {
  const $ = _c(19);
  const {
    children
  } = props;
  const lastMessageId = useRef(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Set([defaultLoadingContext]);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [loadingContexts, setLoadingContexts] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [loadingMessages, setLoadingMessages] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setTimeout(() => {
        setLoadingContexts(_temp);
      }, 300);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const removeDefaultLoadingContextDelayed = useEventCallback(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = message => {
      setLoadingMessages(oldLoadingMessages => {
        const newLoadingMessages = [...oldLoadingMessages, {
          id: lastMessageId.current = lastMessageId.current + 1,
          message
        }];
        return newLoadingMessages.slice(Math.max(newLoadingMessages.length - 5, 0));
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const addLoadingMessage = useEventCallback(t3);
  let t4;
  if ($[4] !== loadingContexts || $[5] !== removeDefaultLoadingContextDelayed) {
    t4 = () => {
      const context = Symbol("loadingContext");
      let newLoadingContexts = loadingContexts.add(context);
      if (loadingContexts.count() === 0) {
        newLoadingContexts = newLoadingContexts.add(defaultLoadingContext);
        setLoadingMessages([]);
      }
      setLoadingContexts(newLoadingContexts);
      removeDefaultLoadingContextDelayed();
      return {
        dispose: () => {
          setLoadingContexts(oldValue => oldValue.delete(context));
        }
      };
    };
    $[4] = loadingContexts;
    $[5] = removeDefaultLoadingContextDelayed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const startLoading = useEventCallback(t4);
  let t5;
  if ($[7] !== addLoadingMessage || $[8] !== startLoading) {
    t5 = {
      addLoadingMessage,
      startLoading
    };
    $[7] = addLoadingMessage;
    $[8] = startLoading;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const contextValue = t5;
  const t6 = loadingContexts.count() > 0;
  let t7;
  if ($[10] !== loadingMessages) {
    t7 = style => <animated.div className={styles.loadingOverlay} style={style}><div className={styles.messagesContainer}>{loadingMessages.map(_temp2)}</div><Spinner className={styles.spinner} /></animated.div>;
    $[10] = loadingMessages;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Fade show={t6} showImmediately={true}>{t7}</Fade>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== children || $[16] !== contextValue || $[17] !== t8) {
    t9 = <PageLoadingOverlayContext.Provider value={contextValue}>{children}{t8}</PageLoadingOverlayContext.Provider>;
    $[15] = children;
    $[16] = contextValue;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
});
function _temp(oldLoadingContexts) {
  if (!oldLoadingContexts.includes(defaultLoadingContext)) {
    return oldLoadingContexts;
  }
  return oldLoadingContexts.delete(defaultLoadingContext);
}
function _temp2(m) {
  return <div key={m.id} className={styles.message}>{m.message}</div>;
}